'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useLocale, LOCALE_MAP, type LocaleConfig } from '@/contexts/LocaleContext';

const COUNTRY_FLAGS: Record<string, string> = {
  AE: '🇦🇪',
  SA: '🇸🇦',
  KW: '🇰🇼',
  QA: '🇶🇦',
  BH: '🇧🇭',
  OM: '🇴🇲',
  EG: '🇪🇬',
  JO: '🇯🇴',
  LB: '🇱🇧',
  US: '🇺🇸',
  GB: '🇬🇧',
  EU: '🇪🇺',
};

const LOCALE_OPTIONS: { code: string; label: string; labelEn: string }[] = [
  { code: 'AE', label: 'الإمارات', labelEn: 'UAE' },
  { code: 'SA', label: 'السعودية', labelEn: 'Saudi Arabia' },
  { code: 'KW', label: 'الكويت', labelEn: 'Kuwait' },
  { code: 'QA', label: 'قطر', labelEn: 'Qatar' },
  { code: 'BH', label: 'البحرين', labelEn: 'Bahrain' },
  { code: 'OM', label: 'عُمان', labelEn: 'Oman' },
  { code: 'EG', label: 'مصر', labelEn: 'Egypt' },
  { code: 'JO', label: 'الأردن', labelEn: 'Jordan' },
  { code: 'US', label: 'الولايات المتحدة', labelEn: 'United States' },
  { code: 'GB', label: 'المملكة المتحدة', labelEn: 'United Kingdom' },
  { code: 'EU', label: 'أوروبا', labelEn: 'Europe' },
];

export default function LocaleSwitcher() {
  const { locale, setLocale, isDetecting } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (code: string) => {
    const selected = LOCALE_MAP[code];
    if (selected) {
      setLocale(selected);
    }
    setOpen(false);
  };

  const flag = COUNTRY_FLAGS[locale.countryCode] || '🌍';
  const isArabic = locale.language === 'ar';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-150 text-sm"
        title={isArabic ? 'تغيير الموقع والعملة' : 'Change location & currency'}
        suppressHydrationWarning
      >
        {isDetecting ? (
          <Globe size={15} className="text-gray-400 animate-pulse" />
        ) : (
          <span className="text-base leading-none">{flag}</span>
        )}
        <span className="hidden sm:inline text-xs font-600 text-gray-700">
          {locale.currencySymbol}
        </span>
        <ChevronDown size={13} className={`text-gray-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`absolute top-full mt-1.5 w-52 bg-white border border-gray-100 rounded-xl shadow-lg z-50 py-1 max-h-72 overflow-y-auto ${locale.dir === 'rtl' ? 'right-0' : 'left-0'}`}>
          <div className="px-3 py-2 border-b border-gray-50">
            <p className="text-xs text-gray-400 font-500">
              {isArabic ? 'اختر بلدك' : 'Select your country'}
            </p>
          </div>
          {LOCALE_OPTIONS.map(opt => {
            const isSelected = locale.countryCode === opt.code;
            return (
              <button
                key={opt.code}
                onClick={() => handleSelect(opt.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors ${isSelected ? 'bg-primary-50' : ''}`}
              >
                <span className="text-base">{COUNTRY_FLAGS[opt.code]}</span>
                <span className={`flex-1 text-start font-500 ${isSelected ? 'text-primary-600' : 'text-gray-700'}`}>
                  {isArabic ? opt.label : opt.labelEn}
                </span>
                <span className="text-xs text-gray-400">{LOCALE_MAP[opt.code]?.currencySymbol}</span>
                {isSelected && <Check size={14} className="text-primary-500 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
